import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveMembershipMutation,
  SaveMembershipInput,
  useMembershipFormQuery,
  Query_Membership_OptionsInput
} from 'generated/graphql';

export type IFormState = {
  id: string;
  personId: string;
  projectId: string;
  roles: string[];
};

const initialState: IFormState = {
  id: '',
  personId: '',
  projectId: '',
  roles: []
};

export function useFormState(personId: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'Id',
        type: 'text'
      },
      personId: {
        label: 'Person id',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      roles: {
        label: 'Roles',
        type: 'text'
      }
    }
  });

  const { data, called, loading } = useQuery({
    filter: {
      personId,
      projectId
    }
  });

  useEffect(() => {
    if (called && !loading && data.id !== '' && formState.id === '') {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveMembership() {
  const [
    fnMutate,
    tuple
  ] = useSaveMembershipMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveMembershipInput) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData extends IFormState {}

export function useQuery(options: Query_Membership_OptionsInput) {
  const skip = options.filter.personId === '' || options.filter.projectId === '';
  const tuple = useMembershipFormQuery({
    variables: {
      options
    },
    skip: skip
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.membership?.id ?? '',
        personId: tuple.variables?.options.filter.personId ?? '',
        projectId: tuple.variables?.options.filter.projectId ?? '',
        roles: tuple.data?.membership?.roles.map((node) => node.id) ?? []
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
